'use client';
import React, { useState } from 'react';
import { faLongArrowRight } from '@fortawesome/pro-light-svg-icons/faLongArrowRight';

import styled from '@benzinga/themetron';
import { Icon } from '@benzinga/core-ui';
import classnames from 'classnames';
import { faEnvelope } from '@fortawesome/pro-light-svg-icons/faEnvelope';
import { SessionContext } from '@benzinga/session-context';
import { emailRegEx } from '../../utils/regexes';

import { ContentManager } from '@benzinga/content-manager';
import { StyleOption } from './index';
import { runningClientSide } from '@benzinga/utils';
import { TrackingManager } from '@benzinga/tracking-manager';

export interface BeehiivFormProps {
  pub_id?: string;
  onSuccess?: (message: string, email?: string) => void;
  redirectUrl?: string;
  styleOption?: StyleOption;
  placeholderTitle?: string;
}

export const BeehiivForm = ({
  onSuccess,
  placeholderTitle = 'Email',
  pub_id = '',
  redirectUrl,
  styleOption = 'default',
}: BeehiivFormProps) => {
  const session = React.useContext(SessionContext);
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>('');
  const [, setResponseSuccessMessage] = React.useState<string>('');

  const handleSubmit = () => {
    setError(null);
    if (!emailRegEx.test(email)) {
      setError('Invalid email.');
      return;
    }

    const params = new URL(`${window.location}`).searchParams;

    const beehiiv_pub_id = pub_id.startsWith('pub_') ? pub_id : 'pub_' + pub_id;

    session
      .getManager(ContentManager)
      .subscribeToNewsletter({
        email: email,
        pub_id: beehiiv_pub_id,
        url: window.location.href,
        utm_campaign: params.get('utm_campaign'),
        utm_medium: params.get('utm_medium'),
        utm_source: params.get('utm_source'),
      })
      .then(res => {
        if (res.ok) {
          const { message, status } = res.ok;
          if (status === 'success') {
            setResponseSuccessMessage('Thanks for submitting the form.');
            session.getManager(TrackingManager).trackFormEvent('submit', 'beehiiv-form-sign-up', {
              email: email,
              form_id: beehiiv_pub_id,
              form_type: 'Beehiiv Form',
            });
            if (onSuccess) {
              const event = new CustomEvent('form_submit', { detail: { email, pub_id } });
              window.dispatchEvent(event);
              setEmail('');
              onSuccess(message, email);
            }
            if (redirectUrl && runningClientSide()) {
              window.open(redirectUrl, '_blank');
            }
          } else {
            setError('Something went wrong.');
            console.log('Something went wrong.');
          }
        } else {
          setError('Something went wrong!!');
          console.log('Something went wrong!!');
        }
      })
      .catch(err => {
        console.log('Something went wrong!');
        console.log(err);
      });
  };

  return (
    <BeehiivEmailContainer className={classnames('beehiiv-form-wrapper', styleOption)}>
      <div className={classnames('beehiiv-form', { [`beehiiv-form-error`]: error })}>
        {pub_id && (
          <React.Fragment>
            <Icon className={'beehiiv-email-icon'} icon={faEnvelope} />
            <input
              className="beehiiv-input w-full text-sm px-2 leading-none placeholder-white bg-transparent border-none ring-0"
              onChange={e => {
                setError(null);
                setEmail(e.target.value);
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  handleSubmit();
                }
              }}
              onSubmit={handleSubmit}
              placeholder={placeholderTitle}
              type="email"
              value={email}
            />
            <button
              aria-label="Click to submit email"
              className={
                styleOption === 'secondary'
                  ? `button py-1.5 px-2 bg-white rounded cursor-pointer select-none
                    active:translate-y-0.3 active:[box-shadow:0_0px_0_0_#cecece]
                    active:border-b-[0px]
                    transition-all duration-150 [box-shadow:0_1px_0_0_#cecece]
                    border-b-[1px] border-blue-400
                  `
                  : 'focus:outline-none py-1.5 px-2 inline-flex items-center justify-center bg-yellow-500 text-white rounded beehiiv-form-button'
              }
              onClick={handleSubmit}
              type="button"
            >
              <Icon className={styleOption === 'secondary' ? 'text-gray-800' : 'text-white'} icon={faLongArrowRight} />
            </button>
          </React.Fragment>
        )}
      </div>
      {error && (
        <div className="p-1">
          <span className="text-red-500 bold">{error}</span>
        </div>
      )}
    </BeehiivEmailContainer>
  );
};

const BeehiivEmailContainer = styled.div`
  &.default {
    width: 100%;
    input.beehiiv-input {
      outline: none !important;
      box-shadow: none !important;
    }

    .beehiiv-form {
      padding: 0.25rem;
      padding-left: 0.625rem;
      display: inline-flex;
      background-color: rgba(206, 221, 242, 0.1);
      border: solid 1px rgba(184, 203, 229, 0.7);
      border-radius: 0.25rem;
      align-items: center;
      width: 100%;
    }
  }

  &.secondary {
    .beehiiv-form {
      position: relative;
      display: flex;
      align-items: center;
      height: 42px;
      background: rgba(242, 247, 255, 0.1);
      border: 1px solid rgba(242, 247, 255, 0.1);
      border-radius: 4px;
      padding: 4px 4px 4px 8px;
      transition-property: background-color, border-color;
      transition-timing-function: ease-in-out;
      transition-duration: 0.25s;
    }

    .beehiiv-form-error {
      border-color: #ff4d4d;
      background: #ff4d4d33;
    }

    .beehiiv-email-icon {
      color: #ffffff;
    }

    .beehiiv-input {
      font-size: 14px;
      line-height: 20px;
      letter-spacing: 0.01em;
      color: #f2f8ff;
      outline: none !important;
      box-shadow: none !important;

      &::placeholder {
        color: #f2f8ff;
      }
    }

    .beehiiv-form-button {
      background-color: #ffffff;

      div {
        color: #3f83f8;
      }
    }

    .error-message-wrapper {
      position: relative;
      width: calc(100% - 16px);
      margin: 0 auto;
      border-radius: 0 0 4px 4px;
      z-index: 1;
      background: #ff4d4d;
      height: 32px;
      max-height: 0;
      transition: 0.25s max-height ease-in-out;
      overflow: hidden;
      font-weight: 400;
      font-size: 12px;
      line-height: 16px;
      display: flex;
      align-items: center;

      span {
        padding: 8px 12px;
      }
    }

    .error-message-active {
      max-height: 32px;
    }
  }
`;
